// F1 Track Detail Page
import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useF1Championship } from '../../hooks/useF1Championship';
import F1PageTemplate from '../../components/f1/F1PageTemplate';
import { TableChart } from '../../components/charts';

const F1CircuitDetailPage = () => {
  const { circuitId } = useParams();
  const { data, loading, error } = useF1Championship();

  if (loading) {
    return (
      <div className="min-h-screen theme-bg-secondary flex items-center justify-center">
        <div className="text-center">
          <div className="text-2xl font-semibold theme-primary mb-4">Loading Track Data...</div>
          <div className="theme-secondary">Please wait while we load track information</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen theme-bg-secondary flex items-center justify-center">
        <div className="text-center">
          <div className="text-2xl font-semibold text-red-600 mb-4">Error Loading Track</div>
          <div className="theme-secondary">{error}</div>
          <Link to="/f1/circuits" className="mt-4 inline-block px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700">
            Back to Circuits
          </Link>
        </div>
      </div>
    );
  }

  // Find specific circuit
  const circuit = data?.circuits?.find(c => c.circuitId == circuitId);
  
  if (!circuit) {
    return (
      <div className="min-h-screen theme-bg-secondary flex items-center justify-center">
        <div className="text-center">
          <div className="text-2xl font-semibold theme-primary mb-4">Track Not Found</div>
          <div className="theme-secondary mb-4">Circuit with ID {circuitId} could not be found.</div>
          <Link to="/f1/circuits" className="inline-block px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700">
            Back to Circuits
          </Link>
        </div>
      </div>
    );
  }

  // Get races at this circuit
  const circuitRaces = data?.races?.filter(race => race.circuitId == circuitId) || [];

  // Transform race data to make fields clickable
  const transformedCircuitRaces = circuitRaces.map(race => ({
    ...race,
    year: (
      <Link 
        to={`/f1/seasons/${race.year}`}
        className="theme-primary hover:theme-accent hover:underline font-medium"
      >
        {race.year}
      </Link>
    ),
    name: (
      <Link 
        to={`/f1/races/${race.raceId}`}
        className="theme-primary hover:theme-accent hover:underline font-medium"
      >
        {race.year} {race.name}
      </Link>
    ),
    winner: race.winnerDriverId ? (
      <Link 
        to={`/f1/drivers/${race.winnerDriverId}`}
        className="theme-primary hover:theme-accent hover:underline font-medium"
      >
        {race.winner}
      </Link>
    ) : race.winner
  }));

  const stats = [
    { value: circuit.name || 'Unknown', label: 'Circuit Name', color: 'theme-primary' },
    { value: circuit.country || 'Unknown', label: 'Country', color: 'theme-primary' },
    { value: circuit.location || 'Unknown', label: 'Location', color: 'theme-primary' },
    { value: circuitRaces.length, label: 'Total Races', color: 'theme-primary' },
    { value: circuit.length ? `${circuit.length}km` : 'Unknown', label: 'Track Length', color: 'theme-primary' },
    { value: new Set(circuitRaces.map(race => race.year)).size || 0, label: 'Years Active', color: 'theme-primary' }
  ];

  return (
    <F1PageTemplate
      title={circuit.name || 'Track Details'}
      subtitle="Circuit Information and Race History"
      summary={['Track details', 'Race statistics', 'Historical data']}
      stats={stats}
    >
      <div className="space-y-8">
        {/* Track Information */}
        <div className="card">
          <h3 className="text-xl font-semibold theme-primary mb-4">Track Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <span className="font-medium theme-secondary">Circuit ID:</span>
              <span className="ml-2 theme-primary">{circuit.circuitId}</span>
            </div>
            <div>
              <span className="font-medium theme-secondary">Circuit Name:</span>
              <span className="ml-2 theme-primary">{circuit.name}</span>
            </div>
            <div>
              <span className="font-medium theme-secondary">Country:</span>
              <span className="ml-2 theme-primary">{circuit.country}</span>
            </div>
            <div>
              <span className="font-medium theme-secondary">Location:</span>
              <span className="ml-2 theme-primary">{circuit.location}</span>
            </div>
            <div>
              <span className="font-medium theme-secondary">Track Length:</span>
              <span className="ml-2 theme-primary">{circuit.length ? `${circuit.length}km` : 'Unknown'}</span>
            </div>
            <div>
              <span className="font-medium theme-secondary">URL:</span>
              <span className="ml-2 theme-primary">
                {circuit.url ? (
                  <a href={circuit.url} target="_blank" rel="noopener noreferrer" className="theme-accent hover:underline">
                    {circuit.url}
                  </a>
                ) : 'Unknown'}
              </span>
            </div>
          </div>
        </div>

        {/* Race History */}
        {circuitRaces.length > 0 && (
          <div className="card">
            <h3 className="text-xl font-semibold theme-primary mb-4">Race History</h3>
            <p className="theme-secondary mb-4">
              All Formula 1 races held at this circuit, including winners, podium finishes, 
              and championship battles throughout the years.
            </p>
            <TableChart 
              data={transformedCircuitRaces}
              config={{
                columns: [
                  { key: 'year', label: 'Year' },
                  { key: 'round', label: 'Round' },
                  { key: 'name', label: 'Race Name' },
                  { key: 'date', label: 'Date' },
                  { key: 'winner', label: 'Winner' }
                ],
                sortable: true,
                sortBy: 'year',
                sortDirection: 'desc',
                pagination: true,
                pageSize: 10
              }}
            />
          </div>
        )}

        {/* Navigation */}
        <div className="card">
          <div className="flex justify-between items-center">
            <Link to="/f1/circuits" className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700">
              ← Back to All Circuits
            </Link>
            <div className="text-sm theme-secondary">
              Circuit ID: {circuitId}
            </div>
          </div>
        </div>
      </div>
    </F1PageTemplate>
  );
};

export default F1CircuitDetailPage;
